import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePCAStore } from '../stores/pcaStore';
import { QUIZ_QUESTIONS, calculateDimensionScores } from '../data/quizQuestions';
import {
  QUESTIONNAIRE_VERSION,
  ROLE_OPTIONS,
  EXPERIENCE_OPTIONS,
  COUNTRY_OPTIONS,
  generateResponseId,
  saveUserResponse,
  type UserDemographics,
  type UserQuizResponse
} from '../data/questionnaireMetadata';

type QuizStep = 'intro' | 'consent' | 'demographics' | 'thanks' | 'questions' | 'results';

export function QuizModal() {
  const { isQuizActive, setQuizActive, setUserAnswer, setUserCoordinates, userAnswers, clearUserData, pcaData } = usePCAStore();
  const [currentStep, setCurrentStep] = useState<QuizStep>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  
  // Consent and demographics state
  const [consentedToShare, setConsentedToShare] = useState<boolean | null>(null);
  const [demographics, setDemographics] = useState<UserDemographics>({
    country: '',
    role: '',
    customRole: '',
    yearsExperience: '',
    inspirationalVoices: []
  });
  const [showOtherRoleInput, setShowOtherRoleInput] = useState(false);
  const [guestSearchQuery, setGuestSearchQuery] = useState('');
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Refs for click-outside handling
  const guestDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // Get all guest names for inspirational voices dropdown
  const allGuests = useMemo(() => {
    if (!pcaData) return [];
    return pcaData.guestCoordinates.map(g => g.guest).sort();
  }, [pcaData]);

  // Filter guests based on search
  const filteredGuests = useMemo(() => {
    if (!guestSearchQuery.trim()) return allGuests.slice(0, 20);
    const query = guestSearchQuery.toLowerCase();
    return allGuests.filter(g => g.toLowerCase().includes(query)).slice(0, 20);
  }, [allGuests, guestSearchQuery]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearchQuery.trim()) return [...COUNTRY_OPTIONS];
    const query = countrySearchQuery.toLowerCase();
    return COUNTRY_OPTIONS.filter(c => c.toLowerCase().includes(query));
  }, [countrySearchQuery]);

  // Click-outside handler for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target as Node)) {
        setShowGuestDropdown(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync answers with store
  useEffect(() => {
    setAnswers(userAnswers);
  }, [userAnswers]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isQuizActive && currentStep === 'intro') {
      setCurrentQuestionIndex(0);
      setAnswers({});
      setConsentedToShare(null);
      setDemographics({
        country: '',
        role: '',
        customRole: '',
        yearsExperience: '',
        inspirationalVoices: []
      });
      setShowOtherRoleInput(false);
      setDemographicsError(null);
    }
  }, [isQuizActive, currentStep]);

  // Validation state for demographics
  const [demographicsError, setDemographicsError] = useState<string | null>(null);

  // Clear error when user fills in fields
  useEffect(() => {
    if (demographicsError) {
      setDemographicsError(null);
    }
  }, [demographics.country, demographics.role, demographics.customRole, demographics.yearsExperience]);

  const handleStartQuiz = () => {
    setCurrentStep('consent');
  };

  const handleConsentYes = () => {
    setConsentedToShare(true);
    setCurrentStep('demographics');
  };

  const handleConsentNo = () => {
    setConsentedToShare(false);
    setCurrentStep('questions');
  };

  const handleDemographicsComplete = () => {
    // Validate required fields
    const missingFields: string[] = [];
    
    if (!demographics.country) missingFields.push('country');
    if (!demographics.role && !demographics.customRole) missingFields.push('role');
    if (!demographics.yearsExperience) missingFields.push('years of experience');
    
    if (missingFields.length > 0) {
      setDemographicsError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    
    setDemographicsError(null);
    setCurrentStep('thanks');
  };

  const handleSkipDemographics = () => {
    // User chose to skip - don't save demographics, go straight to questions
    setConsentedToShare(false);
    setCurrentStep('questions');
  };
  
  const handleThanksComplete = () => {
    setCurrentStep('questions');
  };

  const handleAnswer = (questionId: string, value: number) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    setUserAnswer(questionId, value);
    
    // Move to next question
    if (currentQuestionIndex < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Quiz complete - calculate results
      handleCompleteQuiz(newAnswers);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSkip = () => {
    // Move to next question without answering
    if (currentQuestionIndex < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleCompleteQuiz(answers);
    }
  };

  const handleCompleteQuiz = (finalAnswers: Record<string, number>) => {
    // Calculate dimension scores
    const scores = calculateDimensionScores(finalAnswers);
    
    // Store the 20D coordinates
    setUserCoordinates(scores);
    
    // Save response if consented
    if (consentedToShare) {
      const response: UserQuizResponse = {
        id: generateResponseId(),
        questionnaireVersion: QUESTIONNAIRE_VERSION,
        timestamp: new Date().toISOString(),
        consentedToShare: true,
        demographics: {
          ...demographics,
          role: demographics.role === 'Other' ? demographics.customRole || 'Other' : demographics.role
        },
        answers: finalAnswers,
        coordinates: scores
      };
      saveUserResponse(response);
    }
    
    setCurrentStep('results');
  };

  const handleClose = () => {
    setQuizActive(false);
    setCurrentStep('intro');
    setCurrentQuestionIndex(0);
  };

  const handleRetake = () => {
    clearUserData();
    setCurrentStep('intro');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setConsentedToShare(null);
    setDemographics({
      country: '',
      role: '',
      customRole: '',
      yearsExperience: '',
      inspirationalVoices: []
    });
  };

  const toggleInspirationalVoice = (guest: string) => {
    const current = demographics.inspirationalVoices;
    if (current.includes(guest)) {
      setDemographics({
        ...demographics,
        inspirationalVoices: current.filter(g => g !== guest)
      });
    } else if (current.length < 6) {
      setDemographics({
        ...demographics,
        inspirationalVoices: [...current, guest]
      });
    }
  };

  if (!isQuizActive) return null;

  const currentQuestion = QUIZ_QUESTIONS[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / QUIZ_QUESTIONS.length) * 100;

  return (
    <AnimatePresence>
      {isQuizActive && (
        <motion.div
          className="fixed inset-0 z-[300] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            className="relative bg-warm-bg rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-warm-surface-hover hover:bg-warm-border/50 transition-colors z-10"
            >
              <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="p-8">
              {/* INTRO STEP */}
              {currentStep === 'intro' && (
                <div className="text-center">
                  <div className="text-5xl mb-4">🧭</div>
                  <h2 className="text-2xl font-bold text-text-primary mb-3">
                    Curious where you'd land?
                  </h2>
                  <p className="text-text-secondary mb-6">
                    A few quick questions and we'll plot you among 297 of Lenny's guests.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm text-text-muted mb-8">
                    <span>⏱️ ~3 min</span>
                    <span>•</span>
                    <span>🔒 Just for you</span>
                  </div>
                  <button
                    onClick={handleStartQuiz}
                    className="px-8 py-3 bg-orange-primary text-white rounded-lg font-medium hover:bg-orange-primary/90 transition-colors"
                  >
                    Let's Go! →
                  </button>
                </div>
              )}

              {/* CONSENT STEP */}
              {currentStep === 'consent' && (
                <div className="text-center">
                  <div className="text-5xl mb-5">🗺️</div>
                  <h2 className="text-2xl font-bold text-text-primary mb-3">
                    Want to join the map?
                  </h2>
                  <p className="text-text-secondary mb-2">
                    Share a bit about yourself to help us paint a richer picture of the PM community.
                  </p>
                  <p className="text-sm text-text-muted mb-8">
                    Just a few quick questions — totally anonymous, totally optional ✨
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={handleConsentYes}
                      className="w-full sm:w-auto px-6 py-3 bg-orange-primary text-white rounded-lg font-medium hover:bg-orange-primary/90 transition-colors"
                    >
                      Sure, count me in!
                    </button>
                    <button
                      onClick={handleConsentNo}
                      className="w-full sm:w-auto px-6 py-3 border border-warm-border rounded-lg font-medium text-text-secondary hover:bg-warm-surface-hover transition-colors"
                    >
                      Skip to Quiz
                    </button>
                  </div>
                </div>
              )}

              {/* DEMOGRAPHICS STEP */}
              {currentStep === 'demographics' && (
                <div>
                  <h2 className="text-2xl font-bold text-text-primary mb-2">
                    A few quick questions
                  </h2>
                  <p className="text-sm text-text-muted mb-6">
                    All fields are optional. This helps us understand who's exploring the PM space.
                  </p>

                  <div className="space-y-5">
                    {/* Country */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        What country do you consider yourself most from?
                      </label>
                      <div className="relative" ref={countryDropdownRef}>
                        <input
                          type="text"
                          value={countrySearchQuery || demographics.country}
                          onChange={(e) => {
                            setCountrySearchQuery(e.target.value);
                            setShowCountryDropdown(true);
                          }}
                          onFocus={() => setShowCountryDropdown(true)}
                          placeholder="Search countries..."
                          className="w-full px-4 py-2.5 bg-warm-surface border border-warm-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none focus:ring-1 focus:ring-orange-primary/20"
                        />
                        {showCountryDropdown && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-warm-bg border border-warm-border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                            {filteredCountries.map(country => (
                              <button
                                key={country}
                                onClick={() => {
                                  setDemographics({ ...demographics, country });
                                  setCountrySearchQuery('');
                                  setShowCountryDropdown(false);
                                }}
                                className="w-full text-left px-4 py-1.5 hover:bg-warm-surface-hover text-sm text-text-primary"
                              >
                                {country}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {demographics.country && !showCountryDropdown && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-3 py-1 bg-orange-primary/10 text-orange-primary rounded-full text-sm">
                            {demographics.country}
                          </span>
                          <button
                            onClick={() => setDemographics({ ...demographics, country: '' })}
                            className="text-text-muted hover:text-text-secondary text-sm"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        What best describes your role?
                      </label>
                      {!showOtherRoleInput ? (
                        <div className="flex flex-wrap gap-2">
                          {ROLE_OPTIONS.map(role => (
                            <button
                              key={role}
                              onClick={() => {
                                if (role === 'Other') {
                                  setShowOtherRoleInput(true);
                                  setDemographics({ ...demographics, role: 'Other' });
                                } else {
                                  setDemographics({ ...demographics, role, customRole: '' });
                                }
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                                demographics.role === role
                                  ? 'bg-orange-primary text-white'
                                  : 'bg-warm-surface border border-warm-border text-text-secondary hover:border-orange-primary/50'
                              }`}
                            >
                              {role}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={demographics.customRole}
                            onChange={(e) => setDemographics({ ...demographics, customRole: e.target.value })}
                            placeholder="Enter your role..."
                            autoFocus
                            className="flex-1 px-4 py-2.5 bg-warm-surface border border-warm-border rounded-lg text-text-primary focus:border-orange-primary focus:outline-none focus:ring-1 focus:ring-orange-primary/20"
                          />
                          <button
                            onClick={() => {
                              setShowOtherRoleInput(false);
                              setDemographics({ ...demographics, role: '', customRole: '' });
                            }}
                            className="px-3 py-2.5 text-text-muted hover:text-text-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Years of Experience */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Total years of professional experience
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {EXPERIENCE_OPTIONS.map(exp => (
                          <button
                            key={exp.value}
                            onClick={() => setDemographics({ ...demographics, yearsExperience: exp.value })}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                              demographics.yearsExperience === exp.value
                                ? 'bg-orange-primary text-white'
                                : 'bg-warm-surface border border-warm-border text-text-secondary hover:border-orange-primary/50'
                            }`}
                          >
                            {exp.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Inspirational Voices */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Who are some top Product voices you take inspiration from?
                      </label>
                      <p className="text-xs text-text-muted mb-2">
                        Select up to 6 from Lenny's guests (optional)
                      </p>
                      <div className="relative" ref={guestDropdownRef}>
                        {/* Selected guests as tags + search input */}
                        <div 
                          className="min-h-[46px] px-3 py-2 bg-warm-surface border border-warm-border rounded-lg flex flex-wrap items-center gap-2 focus-within:border-orange-primary focus-within:ring-1 focus-within:ring-orange-primary/20"
                          onClick={() => setShowGuestDropdown(true)}
                        >
                          {demographics.inspirationalVoices.map(guest => (
                            <span
                              key={guest}
                              className="px-2 py-0.5 bg-orange-primary/10 text-orange-primary rounded-full text-sm flex items-center gap-1"
                            >
                              {guest}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleInspirationalVoice(guest);
                                }}
                                className="hover:text-orange-primary/70 ml-0.5"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={guestSearchQuery}
                            onChange={(e) => {
                              setGuestSearchQuery(e.target.value);
                              setShowGuestDropdown(true);
                            }}
                            onFocus={() => setShowGuestDropdown(true)}
                            placeholder={demographics.inspirationalVoices.length === 0 ? "Search guests..." : ""}
                            className="flex-1 min-w-[100px] bg-transparent text-text-primary outline-none text-sm"
                          />
                          <span className="text-xs text-text-muted ml-auto">
                            {demographics.inspirationalVoices.length}/6
                          </span>
                        </div>
                        {showGuestDropdown && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-warm-bg border border-warm-border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                            {filteredGuests.map(guest => {
                              const isSelected = demographics.inspirationalVoices.includes(guest);
                              const canSelect = demographics.inspirationalVoices.length < 6 || isSelected;
                              return (
                                <button
                                  key={guest}
                                  onClick={() => {
                                    toggleInspirationalVoice(guest);
                                    setGuestSearchQuery('');
                                    // Keep dropdown open for multi-select
                                  }}
                                  disabled={!canSelect}
                                  className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                                    isSelected
                                      ? 'bg-orange-primary/10 text-orange-primary'
                                      : canSelect
                                        ? 'hover:bg-warm-surface-hover text-text-primary'
                                        : 'opacity-50 cursor-not-allowed text-text-muted'
                                  }`}
                                >
                                  {guest}
                                  {isSelected && ' ✓'}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Validation error */}
                  {demographicsError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      {demographicsError}
                    </div>
                  )}

                  {/* Continue and Skip buttons */}
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={handleSkipDemographics}
                      className="px-4 py-2.5 text-text-secondary hover:text-text-primary text-sm transition-colors"
                    >
                      Forget it, skip →
                    </button>
                    <button
                      onClick={handleDemographicsComplete}
                      className="px-6 py-2.5 bg-orange-primary text-white rounded-lg font-medium hover:bg-orange-primary/90 transition-colors"
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* THANKS STEP - Brief transition */}
              {currentStep === 'thanks' && (
                <motion.div 
                  className="text-center py-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-orange-primary/10 flex items-center justify-center">
                    <svg className="w-7 h-7 text-orange-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-text-primary mb-2">
                    Thank you!
                  </h2>
                  <p className="text-text-secondary mb-6">
                    Your contribution helps map the PM landscape.
                  </p>
                  <p className="text-sm text-text-muted mb-8">
                    Now let's find where you fit in the space...
                  </p>
                  <button
                    onClick={handleThanksComplete}
                    className="px-8 py-3 bg-orange-primary text-white rounded-lg font-medium hover:bg-orange-primary/90 transition-colors"
                  >
                    Let's Go →
                  </button>
                </motion.div>
              )}

              {/* QUESTIONS STEP */}
              {currentStep === 'questions' && currentQuestion && (
                <div>
                  {/* Progress bar */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between text-xs text-text-muted mb-2">
                      <span>Question {currentQuestionIndex + 1} of {QUIZ_QUESTIONS.length}</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2 bg-warm-border/30 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-orange-primary"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Question */}
                  <h3 className="text-xl font-semibold text-text-primary mb-6">
                    {currentQuestion.text}
                  </h3>

                  {/* Options */}
                  <div className="space-y-3 mb-6">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAnswer(currentQuestion.id, option.value)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                          answers[currentQuestion.id] === option.value
                            ? 'border-orange-primary bg-orange-primary/10'
                            : 'border-warm-border hover:border-orange-primary/50 hover:bg-warm-surface-hover'
                        }`}
                      >
                        <span className="text-text-secondary">{option.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleBack}
                      disabled={currentQuestionIndex === 0}
                      className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleSkip}
                      className="px-4 py-2 text-sm text-text-muted hover:text-text-secondary transition-colors"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              )}

              {/* RESULTS STEP */}
              {currentStep === 'results' && (
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Quiz Complete!
                  </h2>
                  <p className="text-text-secondary mb-6">
                    Your position has been calculated. Check the 3D space to see where you fit.
                  </p>
                  {consentedToShare && (
                    <p className="text-sm text-text-muted mb-6">
                      Thank you for contributing to the PM landscape mapping! 🙏
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={handleClose}
                      className="px-6 py-2.5 bg-text-primary text-warm-bg rounded-lg font-medium hover:bg-text-secondary transition-colors"
                    >
                      View Results
                    </button>
                    <button
                      onClick={handleRetake}
                      className="px-6 py-2.5 border border-warm-border rounded-lg font-medium text-text-secondary hover:bg-warm-surface-hover transition-colors"
                    >
                      Retake Quiz
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
